import { useMemo } from "react";
import type { TicketDto } from "@/api/tickets";
import { useRequest } from "@/request";

export const AiCapability = {
  Classification: 1,
} as const;

export const AiSuggestionStatus = {
  Pending: 1,
  Completed: 2,
  Failed: 3,
  Superseded: 4,
} as const;

export type ClassificationCategoryCandidate = {
  categoryId: string;
  confidence: number;
  /** 机器可读的理由标识（如 `customer_requests_top_up`），仅作调试信息展示。 */
  reasonCode: string;
  /**
   * 面向坐席的自然语言理由。后端当前的分类 prompt 尚未产出该字段；
   * 一旦 prompt 与 AiClassificationResultJson 补上，界面会自动优先展示它。
   */
  reason?: string;
};

export type ClassificationResult = {
  projectId: string | null;
  categories: ClassificationCategoryCandidate[];
  routeTeamId: string | null;
  overallConfidence: number | null;
};

export type AiSuggestion = {
  id: string;
  ticketId: string;
  capability: number;
  status: number;
  sourceContentVersion: string;
  resultJson: string | null;
  confidence: number | null;
  provider: string;
  model: string;
  promptVersion: string;
  failureCode: string | null;
  creationTime: string;
  completedAt: string | null;
};

export function parseClassificationResult(suggestion: AiSuggestion | null | undefined): ClassificationResult | null {
  if (!suggestion?.resultJson) return null;

  try {
    const result = JSON.parse(suggestion.resultJson) as Partial<ClassificationResult>;
    if (!Array.isArray(result.categories)) return null;

    return {
      projectId: typeof result.projectId === "string" ? result.projectId : null,
      categories: result.categories
        .filter(
          (candidate): candidate is ClassificationCategoryCandidate =>
            typeof candidate?.categoryId === "string" && typeof candidate.confidence === "number" && typeof candidate.reasonCode === "string",
        )
        // 置信度降序：界面依赖「首项即最优」来决定是给结论还是摊开候选。
        .sort((left, right) => right.confidence - left.confidence),
      routeTeamId: typeof result.routeTeamId === "string" ? result.routeTeamId : null,
      overallConfidence: typeof result.overallConfidence === "number" ? result.overallConfidence : null,
    };
  } catch {
    return null;
  }
}

export function useAiSuggestionApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getCurrentClassification: async (ticketId: string, signal?: AbortSignal) =>
        (await request<AiSuggestion | null>(`tickets/${encodeURIComponent(ticketId)}/ai/suggestions/current?capability=${AiCapability.Classification}`, {
          method: "GET",
          signal,
          skipErrorToast: true,
        })) ?? null,
      /**
       * 批量取当前分类建议；待分类队列一屏几十条，逐条打 current 会把候选目录读上几十遍。
       * 只读已有结果，不触发生成——没生成过或文本已改的工单不会出现在返回里。
       */
      getCurrentClassificationBatch: (ticketIds: string[], signal?: AbortSignal) =>
        request<AiSuggestion[]>("ai/suggestions/current-batch", {
          method: "POST",
          data: { ticketIds, capability: AiCapability.Classification },
          signal,
          skipErrorToast: true,
        }),
      classify: (ticketId: string, force = false) =>
        request<AiSuggestion>(`tickets/${encodeURIComponent(ticketId)}/ai/classify?force=${String(force)}`, {
          method: "POST",
          skipErrorToast: true,
          // AI Provider 有自己的服务端超时（OpenRouter 默认 60 秒）；禁用 Axios 的
          // 全局 10 秒超时，避免前端先取消请求并遮住服务端返回的具体错误。
          timeout: 0,
        }),
      applyCategory: (suggestionId: string, categoryId: string, ticketConcurrencyStamp: string) =>
        request<TicketDto>(`ai/suggestions/${encodeURIComponent(suggestionId)}/apply-category`, {
          method: "POST",
          skipErrorToast: true,
          data: { categoryId, ticketConcurrencyStamp },
        }),
    }),
    [request],
  );
}
